import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  input,
  output,
  signal,
  WritableSignal,
} from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { PasswordEyeDirective } from '@catch/directives';
import { ButtonComponent } from '@catch/general-ui';
import { ToastsService } from '@catch/services';
import { TranslocoPipe } from '@jsverse/transloco';
import { InputOtpChangeEvent, InputOtpModule } from 'primeng/inputotp';
import { emailRegex } from '../../../../../../assets/regex';
import { PlatformTfaMethodType } from '../../../../enums/platform-tfa-method-type.enum';
import { PlatformAuthService } from '../../../../services/platform-auth.service';

type LoginStep = 'credentials' | 'totp';

@Component({
  selector: 'app-platform-login-form',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    TranslocoPipe,
    ButtonComponent,
    PasswordEyeDirective,
    InputOtpModule,
  ],
  templateUrl: './platform-login-form.component.html',
  styleUrl: './platform-login-form.component.scss',
})
export class PlatformLoginFormComponent {
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  private readonly auth = inject(PlatformAuthService);
  private readonly toastsService = inject(ToastsService);

  loginForm: FormGroup;
  loginFormSubmitAttempt: WritableSignal<boolean> = signal(false);
  loginAttempt: WritableSignal<boolean> = signal(false);
  loginStep: WritableSignal<LoginStep> = signal('credentials');
  totpInvalid: WritableSignal<boolean> = signal(false);
  totpCode = '';
  cooldown: WritableSignal<number> = signal(0);
  loading = input<boolean>();
  redirectUrl = input<string | null>(null);
  compact = input(false);

  onLoading = output<boolean>();
  loggedIn = output<void>();
  forgotPassword = output<void>();

  loadingEffect = effect(() => {
    this.onLoading.emit(this.loginAttempt());
  });

  constructor() {
    this.loginForm = this.fb.group({
      email: this.fb.control(null, [Validators.required, Validators.pattern(emailRegex)]),
      password: this.fb.control(null, [Validators.required, Validators.minLength(8)]),
      rememberMe: this.fb.control(false),
    });
  }

  /**
   * Submits credentials or the TOTP step.
   */
  onLoginFormSubmit(): void {
    if (this.loginStep() === 'totp') {
      this.submitTotpLogin(this.totpCode);
      return;
    }
    this.loginFormSubmitAttempt.set(true);
    if (this.loginForm.valid && this.cooldown() <= 0) {
      this.loginAttempt.set(true);
      this.auth.getLoginAuthenticationMethods(this.email.value).subscribe({
        next: (response) => {
          this.loginAttempt.set(false);
          if (response.types.includes(PlatformTfaMethodType.APP)) {
            this.loginStep.set('totp');
            this.totpInvalid.set(false);
            this.totpCode = '';
          } else {
            this.dispatchLogin();
          }
          this.cdr.markForCheck();
        },
        error: (error: HttpErrorResponse) => {
          this.loginAttempt.set(false);
          this.toastsService.warning(this.loginErrorKey(error));
          this.cdr.markForCheck();
        },
      });
    }
  }

  /**
   * Completes login with a TOTP code.
   * @param tfaCode - Six-digit authenticator code
   */
  submitTotpLogin(tfaCode?: string): void {
    if (tfaCode?.length !== 6 || this.loginAttempt() || this.cooldown() > 0) {
      return;
    }
    this.loginAttempt.set(true);
    this.totpInvalid.set(false);
    this.dispatchLogin(tfaCode);
  }

  /**
   * Updates the TOTP value and auto-submits a full code.
   * @param event - OTP change event
   */
  onOtpChange(event: InputOtpChangeEvent): void {
    const value = event?.value ?? '';
    this.totpCode = value?.toString?.() ?? String(value);
    if (this.totpCode.length === 6) {
      this.submitTotpLogin(this.totpCode);
    } else if (this.totpInvalid()) {
      this.totpInvalid.set(false);
    }
  }

  /**
   * Returns to the email/password step.
   */
  goBackToCredentials(): void {
    this.loginStep.set('credentials');
    this.loginAttempt.set(false);
    this.totpInvalid.set(false);
    this.totpCode = '';
    this.cdr.markForCheck();
  }

  /**
   * Maps a candidate login error to a translation key.
   * @param error - HTTP error from the login request
   * @returns Translation key
   */
  private loginErrorKey(error: HttpErrorResponse): string {
    const message = typeof error.error?.message === 'string' ? error.error.message : '';
    if (message.includes('Unverified')) {
      return 'jobboard.platform.auth.login.unverified-email';
    }
    if (message.includes('AccountBlocked')) {
      return 'jobboard.platform.auth.login.blocked-account';
    }
    return 'jobboard.platform.auth.login.invalid-credentials';
  }

  /**
   * Starts a cooldown timer after failed login attempts.
   * @param cooldown - Cooldown duration in seconds
   */
  setCooldown(cooldown: number): void {
    this.cooldown.set(cooldown);
    const intervalId = setInterval(() => {
      if (this.cooldown() > 0) {
        this.cooldown.update((value) => value - 1);
      } else {
        clearInterval(intervalId);
      }
      this.cdr.markForCheck();
    }, 1000);
    this.cdr.markForCheck();
  }

  /**
   * Navigates to a redirect target or the dashboard.
   */
  private navigateAfterLogin(): void {
    const redirectUrl = this.redirectUrl();
    if (redirectUrl && redirectUrl !== this.router.url) {
      this.router.navigateByUrl(redirectUrl);
      return;
    }
    if (!redirectUrl) {
      this.router.navigate(['/dashboard']);
    }
  }

  /**
   * Sends login credentials and optional TOTP.
   * @param tfaCode - Authenticator code
   */
  private dispatchLogin(tfaCode?: string): void {
    this.auth
      .login(this.email.value, this.password.value, !!this.rememberMe.value, tfaCode)
      .subscribe({
        next: () => {
          this.loggedIn.emit();
          this.navigateAfterLogin();
          this.loginAttempt.set(false);
          this.cdr.markForCheck();
        },
        error: (error: HttpErrorResponse) => {
          this.loginAttempt.set(false);
          if (this.loginStep() === 'totp') {
            this.totpInvalid.set(true);
            this.totpCode = '';
          } else {
            this.toastsService.warning(this.loginErrorKey(error));
          }
          this.cdr.markForCheck();
        },
      });
  }

  get email() {
    return this.loginForm.get('email')!;
  }

  get password() {
    return this.loginForm.get('password')!;
  }

  get rememberMe() {
    return this.loginForm.get('rememberMe')!;
  }
}
